import type {
  SearchSidekickRecipients,
  SidekickRecipient,
} from '@brightdrop/ai-sidekick';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { rtkClient } from '~/api';
import type { Profile } from '~/api/generated-api';
import { selectActingProfileOrganization } from '~/store/profile/profileSlice.selectors';

const { useLazySearchUserProfilesQuery } = rtkClient;

/** Cap on suggestions, since the field shows a short dropdown, not a roster. */
const MAX_RECIPIENT_RESULTS = 10;

/**
 * Maps a CSP profile onto the shape the Sidekick package consumes.
 *
 * A blank address is no address: it cannot be emailed, and it would
 * reach the field as a chip with nothing in it.
 */
const toRecipient = (profile: Profile): SidekickRecipient | undefined => {
  const email = profile.email?.trim() ?? '';

  if (email === '') {
    return undefined;
  }

  return {
    id: profile.id ?? email,
    email,
    displayName: profile.displayName,
  };
};

/**
 * Builds the recipient lookup the Sidekick share modal calls as the
 * user types.
 *
 * `/users` filters by name and by email through separate parameters,
 * so the typed text picks one: anything containing `@` is an address,
 * everything else is a name. Results are scoped to the organization
 * the user is acting as, and a profile without one gets no matches
 * rather than an unscoped search.
 *
 * The caller's abort signal cancels the underlying request, so only
 * the lookup for the text still in the field stays in flight.
 *
 * @returns A callback resolving to the matching recipients, or an empty list.
 */
const useShareRecipientSearch = (): SearchSidekickRecipients => {
  const [searchUserProfiles] = useLazySearchUserProfilesQuery();
  const organizationId = useSelector(selectActingProfileOrganization)?.id;

  return useCallback<SearchSidekickRecipients>(
    async (query, signal) => {
      // `organizationId` is an optional filter on `/users`, so omitting
      // it widens the search rather than narrowing it to nothing.
      // `organization` is optional on a profile, and one without it has
      // no directory to offer.
      if (organizationId === undefined) {
        return [];
      }

      const isEmailQuery = query.includes('@');
      const request = searchUserProfiles({
        organizationId,
        size: MAX_RECIPIENT_RESULTS,
        ...(isEmailQuery ? { email: query } : { name: query }),
      });

      // A keystroke supersedes the lookup before it, and the caller says
      // so by aborting. Passing that through cancels the request instead
      // of leaving one per keystroke running for a result nobody reads.
      signal?.addEventListener('abort', () => request.abort(), { once: true });

      const response = await request.unwrap();

      return (response.result?.items ?? []).reduce<SidekickRecipient[]>(
        (recipients, profile) => {
          const recipient = toRecipient(profile);
          if (recipient !== undefined) {
            recipients.push(recipient);
          }
          return recipients;
        },
        []
      );
    },
    [organizationId, searchUserProfiles]
  );
};

export default useShareRecipientSearch;
